import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Users } from 'lucide-react';

interface ClubMemberManagementProps {
  clubId: string;
}

export const ClubMemberManagement = ({ clubId }: ClubMemberManagementProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'administrator' | 'coach' | 'parent' | 'player'>('player');
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  // Fetch existing club members
  const { data: clubMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_club_roles')
        .select(`
          id,
          role,
          is_active,
          created_at,
          profiles!inner (
            id,
            email,
            first_name,
            last_name
          )
        `)
        .eq('club_id', clubId)
        .eq('is_active', true);
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch all users for adding new members
  const { data: allUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['all-users', searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('profiles')
        .select('id, email, first_name, last_name')
        .order('first_name');
      
      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }
      
      const { data, error } = await query.limit(50);
      
      if (error) throw error;
      return data;
    },
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase
        .from('user_club_roles')
        .insert([{
          user_id: userId,
          club_id: clubId,
          role: role as 'administrator' | 'coach' | 'parent' | 'player',
        }]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-members', clubId] });
      toast.success('Member added successfully');
      setIsAddDialogOpen(false);
      setSelectedUserId('');
      setSelectedRole('player');
    },
    onError: (error) => {
      toast.error('Failed to add member');
      console.error('Error:', error);
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async (membershipId: string) => {
      const { error } = await supabase
        .from('user_club_roles')
        .update({ is_active: false })
        .eq('id', membershipId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-members', clubId] });
      toast.success('Member removed successfully');
    },
    onError: (error) => {
      toast.error('Failed to remove member');
      console.error('Error:', error);
    },
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ membershipId, newRole }: { membershipId: string; newRole: string }) => {
      const { error } = await supabase
        .from('user_club_roles')
        .update({ role: newRole as Database["public"]["Enums"]["club_role"] })
        .eq('id', membershipId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-members', clubId] });
      toast.success('Role updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update role');
      console.error('Error:', error);
    },
  });

  const handleAddMember = () => {
    if (!selectedUserId || !selectedRole) {
      toast.error('Please select a user and role');
      return;
    }
    
    addMemberMutation.mutate({ userId: selectedUserId, role: selectedRole });
  };

  const handleRemoveMember = (membershipId: string) => {
    if (confirm('Are you sure you want to remove this member?')) {
      removeMemberMutation.mutate(membershipId);
    }
  };

  const handleUpdateRole = (membershipId: string, newRole: string) => {
    updateRoleMutation.mutate({ membershipId, newRole });
  };

  // Filter out users who are already members
  const availableUsers = allUsers?.filter(user => 
    !clubMembers?.some(member => member.profiles.id === user.id)
  ) || [];

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'administrator': return 'bg-red-100 text-red-800';
      case 'coach': return 'bg-blue-100 text-blue-800';
      case 'parent': return 'bg-green-100 text-green-800';
      case 'player': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (membersLoading) {
    return <div>Loading members...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Add Member Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Current Members ({clubMembers?.length || 0})</h3>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Club Member</DialogTitle>
              <DialogDescription>
                Search for a user and assign them a role in this club.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="user-search">Search Users</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="user-search"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="user-select">Select User</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="role-select">Role</Label>
                <Select value={selectedRole} onValueChange={(value: 'administrator' | 'coach' | 'parent' | 'player') => setSelectedRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="administrator">Administrator</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="player">Player</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddMember}
                  disabled={addMemberMutation.isPending}
                >
                  {addMemberMutation.isPending ? 'Adding...' : 'Add Member'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Members Table */}
      {clubMembers && clubMembers.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Added</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clubMembers.map((member) => (
              <TableRow key={member.id}>
                <TableCell className="font-medium">
                  {member.profiles.first_name} {member.profiles.last_name}
                </TableCell>
                <TableCell>{member.profiles.email}</TableCell>
                <TableCell>
                  <Select 
                    value={member.role} 
                    onValueChange={(newRole) => handleUpdateRole(member.id, newRole)}
                  >
                    <SelectTrigger className="w-auto">
                      <Badge className={getRoleBadgeColor(member.role)}>
                        {member.role}
                      </Badge>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="administrator">Administrator</SelectItem>
                      <SelectItem value="coach">Coach</SelectItem>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="player">Player</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {new Date(member.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveMember(member.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="py-8 text-center">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Members Yet</h3>
          <p className="text-muted-foreground mb-4">
            This club doesn't have any members yet. Add the first member to get started.
          </p>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Member
          </Button>
        </div>
      )}
    </div>
  );
};
